import type {
  SellerAuthView,
  SellerPermission,
  StoreCapability,
} from "@/lib/api/types";

export type AccessRule = {
  capability?: StoreCapability;
  permission?: SellerPermission;
  permissions?: readonly SellerPermission[];
  /** At least one of these (where `permissions` needs all of them). */
  anyPermission?: readonly SellerPermission[];
  identity?: boolean;
  storeKind?: SellerAuthView["storeKind"];
};
/** One registry serves route admission and all navigation, including deep links. */
export const STORE_ROUTES: Record<string, AccessRule> = {
  // STORE_VIEW, or ORDER_VIEW alone: Free's order moderator (D2) holds no
  // STORE_* permission and still opens the operational dashboard ("اليوم").
  // The analytics dashboard additionally requires STORE_VIEW (DashboardPage).
  dashboard: { capability: "dashboard", anyPermission: ["STORE_VIEW", "ORDER_VIEW"] },
  orders: { capability: "orders", permission: "ORDER_VIEW" },
  carts: { capability: "checkoutLinks", permission: "ORDER_EDIT", storeKind: "DEFAULT" },
  customers: { capability: "customers", permission: "ORDER_VIEW" },
  products: { capability: "products", permission: "PRODUCT_VIEW" },
  categories: { capability: "categories", permission: "CATEGORY_VIEW" },
  coupons: { capability: "promotions", permission: "COUPON_VIEW" },
  discounts: { capability: "promotions", permission: "COUPON_VIEW" },
  "free-shipping": { capability: "promotions", permission: "COUPON_VIEW" },
  reviews: { capability: "reviews", permission: "REVIEW_VIEW" },
  payments: { capability: "wallet", permission: "PAYMENT_VIEW" },
  wallet: { capability: "wallet", permission: "PAYMENT_VIEW" },
  transactions: { capability: "onlinePayments", permission: "PAYMENT_VIEW" },
  settings: { capability: "storeSettings", permission: "STORE_VIEW" },
  meta: { capability: "storeSettings", permission: "STORE_VIEW", storeKind: "DEFAULT" },
  store: { capability: "design", permission: "DESIGN_VIEW" },
  "quick-setup": { capability: "onboarding", permission: "STORE_EDIT" },
  // "Your plan": open to every member of an admitted store, Free included —
  // it is how a Free seller finds out what Premium is.
  plan: { capability: "wallet", permission: "STORE_VIEW" },
  // Direct-entry aliases. They have no page of their own (analytics lives on
  // the dashboard, the team in settings) but a seller types or bookmarks them,
  // and a denied one must explain itself instead of silently redirecting.
  analytics: { capability: "analytics", permission: "STORE_VIEW" },
  // Admitted on Free in WITHDRAW-ONLY mode (see WITHDRAW_ONLY_SECTIONS).
  team: { capability: "storeSettings", permission: "MEMBER_VIEW" },
  profile: { identity: true },
  account: { identity: true },
};
export const SETTINGS_CAPABILITIES: Record<string, StoreCapability> = {
  branding: "logo",
  design: "design",
  payment: "storeSettings",
  payments: "storeSettings",
  shipping: "shippingFees",
  recovery: "abandonedCartRecovery",
  inventory: "inventory",
  policies: "profileBranding",
  marketing: "storeSettings",
  developer: "marketing",
  team: "storeSettings",
  domain: "storeSettings",
  help: "onboarding",
};
/**
 * Settings sections a store WITHOUT the paid capability may still open, to take
 * things back rather than add them: list and remove team members and revoke
 * invites (`team`), read and disconnect the Meta CAPI connection
 * (`marketing`). Withdrawal is not a paid feature — the backend classifies
 * those handlers `storeSettings` — so a downgraded owner is never locked out of
 * revoking access or credentials. The page renders the list with its
 * Remove/Revoke/Disconnect actions and replaces add/invite/connect with the
 * "with Premium" line linking to the plan screen.
 *
 * Gateway and courier connections follow the same rule inside the payment and
 * shipping sections (paid capability `onlinePayments` / `shipping`), which Free
 * already admits.
 */
export const WITHDRAW_ONLY_SECTIONS: Record<string, StoreCapability> = {
  team: "team",
  marketing: "marketing",
  payment: "onlinePayments",
  payments: "onlinePayments",
  shipping: "shipping",
};

/**
 * True when `section` is open to this store only for withdrawal: the section is
 * admitted, but the capability that adds to it is not. The page then hides
 * add/invite/connect and keeps list/remove/revoke/disconnect.
 */
export function isWithdrawOnly(
  seller: SellerAuthView | undefined,
  section: string,
): boolean {
  const paid = Object.prototype.hasOwnProperty.call(WITHDRAW_ONLY_SECTIONS, section)
    ? WITHDRAW_ONLY_SECTIONS[section]
    : undefined;
  return Boolean(paid) && seller?.access?.capabilities[paid!] !== true;
}

/**
 * Is the signed-in seller the store's OWNER? Buying Premium is the owner's
 * call, so a member who meets a paid feature is told to ask the owner — no
 * price, no top-up. A backend that does not report `ownerSellerId` keeps the
 * owner's view (the behaviour before it existed).
 */
export function isStoreOwner(seller: SellerAuthView | undefined): boolean {
  const owner = seller?.access?.ownerSellerId;
  if (owner == null) return true;
  return owner === seller?.id;
}

export function canAccess(
  seller: SellerAuthView | undefined,
  rule: AccessRule | undefined,
): boolean {
  if (!seller || !rule) return false;
  if (rule.identity) return true;
  return (
    seller.storeAccess === true &&
    Boolean(seller.access) &&
    (!rule.storeKind || seller.storeKind === rule.storeKind) &&
    (!rule.capability ||
      seller.access?.capabilities[rule.capability] === true) &&
    (!rule.permission || seller.permissions.includes(rule.permission)) &&
    (!rule.permissions ||
      rule.permissions.every((permission) =>
        seller.permissions.includes(permission),
      )) &&
    (!rule.anyPermission ||
      rule.anyPermission.some((permission) =>
        seller.permissions.includes(permission),
      ))
  );
}
export function routeAccessRule(relativeUrl: string): AccessRule | undefined {
  const [path, search = ""] = relativeUrl.replace(/^\//, "").split("?");
  const [root, child] = path.split("/");
  const base = Object.prototype.hasOwnProperty.call(STORE_ROUTES, root)
    ? STORE_ROUTES[root]
    : undefined;
  if (!base) return undefined;
  if (
    root === "payments" &&
    new URLSearchParams(search).get("tab") === "online"
  )
    return { ...base, capability: "onlinePayments" };
  if (root === "settings") {
    const section =
      child === "shipping"
        ? "shipping"
        : new URLSearchParams(search).get("section");
    if (section)
      return Object.prototype.hasOwnProperty.call(
        SETTINGS_CAPABILITIES,
        section,
      )
        ? {
            ...base,
            capability: SETTINGS_CAPABILITIES[section],
            ...(section === "shipping"
              ? { permissions: ["SHIPPING_VIEW"] as const }
              : {}),
          }
        : undefined;
  }
  // `/products/new` is the create screen (the list with its create drawer
  // open), so it needs PRODUCT_CREATE — a view-only role gets the in-shell
  // permission card, never `products/:id` reading "new" as a product id.
  if (path === "products/new")
    return { ...base, permission: "PRODUCT_CREATE" };
  if (root === "carts" && child && path.split("/").length === 2)
    return { ...base, permissions: ["ORDER_EDIT", "PRODUCT_VIEW"] };
  // Only declared descendant paths are admitted. New routes must be classified.
  if (
    child &&
    !(
      (root === "products" || root === "categories") &&
      path.split("/").length === 2
    ) &&
    !(
      root === "store" &&
      (path === "store/editor" || path === "store/editor/simple")
    ) &&
    !(root === "settings" && /^settings\/shipping\/areas\/[^/]+$/.test(path))
  )
    return undefined;
  return base;
}
export function sellerLanding(seller: SellerAuthView | undefined): string {
  return (
    [
      "dashboard",
      "orders",
      "products",
      "customers",
      "payments",
      "account",
    ].find((path) => canAccess(seller, STORE_ROUTES[path])) ?? "account"
  );
}
/**
 * What a seller can do in the bound store — WITHOUT the lifecycle `revision`,
 * which moves on every renewal, warning and admin action even when nothing a
 * seller can do has changed.
 */
export function accessShape(seller: SellerAuthView): string {
  return JSON.stringify([
    seller.storeAccess === true,
    seller.access?.capabilities ?? null,
    seller.access?.limits ?? null,
    [...seller.permissions].sort(),
  ]);
}

/**
 * How a re-inspected session differs from the one the portal is showing.
 * - `none`: same identity and same abilities (a revision-only change lands here).
 * - `in-place`: same identity and store, still admitted; capabilities, limits or
 *   permissions moved. The context updates without remounting the portal, so a
 *   half-typed form survives.
 * - `rebind`: another actor or store, or admission itself flipped (paused staff,
 *   restored staff). Private state is discarded and the portal remounts.
 */
export type AccessChange = "none" | "in-place" | "rebind";
export function classifyAccessChange(
  current: SellerAuthView,
  next: SellerAuthView,
): AccessChange {
  if (current.id !== next.id || current.storeSlug !== next.storeSlug)
    return "rebind";
  if (
    (current.storeAccess === true) !== (next.storeAccess === true) ||
    Boolean(current.access) !== Boolean(next.access)
  )
    return "rebind";
  return accessShape(current) === accessShape(next) ? "none" : "in-place";
}

/** Why a route is not admitted — each reason gets its own explanation. */
export type DenialReason = "capability" | "permission" | "unknown";
export function denialReason(
  seller: SellerAuthView | undefined,
  rule: AccessRule | undefined,
): DenialReason {
  if (!rule || !seller?.access) return "unknown";
  if (rule.capability && seller.access.capabilities[rule.capability] !== true)
    return "capability";
  return "permission";
}
